/**
 * Le panneau d'accord.
 *
 * La plupart des valeurs qui pilotent ce jeu sont exposées ici ; le reste est
 * accessible depuis la console par la surface de mise au point. Le compte est
 * dans TECH-DEBT §11, généré plutôt que tapé — cet en-tête a dit « soixante-dix
 * et trente-deux » pendant que l'arbre disait autre chose. L'écart est la dette
 * 11, pas un choix de conception.
 *
 * Généré depuis une table plutôt qu'écrit en balisage : ajouter une valeur doit
 * tenir en une ligne, et chaque rangée se comporte alors pareil — libellé,
 * lecture en direct, remise à zéro, et une note qui dit ce que le nombre fait
 * plutôt que ce qu'il est. Libellés et notes sont de l'interface : en anglais.
 */
import type { Tuning } from '../sim/index.js';

export interface SliderSpec {
  readonly key: keyof Tuning;
  readonly label: string;
  readonly min: number;
  readonly max: number;
  readonly step: number;
  readonly hint: string;
}

export const SLIDERS: readonly SliderSpec[] = [
  {
    key: 'renderScale',
    label: 'Render scale',
    min: 0.6,
    max: 1,
    step: 0.05,
    hint: 'Fraction of the native resolution. Below 0.8 the thin track edges start to break up.',
  },
  {
    key: 'speedMax',
    label: 'Top speed',
    min: 120,
    max: 340,
    step: 2,
    hint: 'In metres per second. 258 is about 930 km/h, boost takes it to 1200.',
  },
  {
    key: 'speedRamp',
    label: 'Ramp-up distance',
    min: 2000,
    max: 20000,
    step: 200,
    hint: 'Distance needed to reach top speed.',
  },
  {
    key: 'yawBase',
    label: 'Steering angle',
    min: 0.15,
    max: 0.8,
    step: 0.01,
    hint: 'Maximum yaw angle at low speed, in radians. It shrinks as speed rises.',
  },
  {
    key: 'yawResponse',
    label: 'Stick response',
    min: 1.5,
    max: 12,
    step: 0.1,
    hint: 'How fast the ship takes the requested angle.',
  },
  {
    key: 'gripHold',
    label: 'Grip',
    min: 0.4,
    max: 4,
    step: 0.05,
    hint: 'How fast the path swings round to follow the nose.',
  },
  {
    key: 'gripLimit',
    label: 'Drift threshold',
    min: 10,
    max: 70,
    step: 1,
    hint: 'Lateral load beyond which grip breaks away, in m/s².',
  },
  {
    key: 'gripDrift',
    label: 'Grip while drifting',
    min: 0.1,
    max: 2,
    step: 0.05,
    hint: 'Grip left once the slide has started.',
  },
  {
    key: 'driftCharge',
    label: 'Drift boost charge',
    min: 0,
    max: 40,
    step: 1,
    hint: 'Boost points regained per second while drifting.',
  },
  {
    key: 'climbSup',
    label: 'Climb to super boost',
    min: 100,
    max: 2000,
    step: 25,
    hint: 'Metres of clean drift under boost that earn a super boost. A wall empties the climb.',
  },
  {
    key: 'climbSurge',
    label: 'Climb to G-SURGE',
    min: 100,
    max: 3000,
    step: 25,
    hint: 'Metres of clean drift under a super boost that open the surge.',
  },
  {
    key: 'climbDecay',
    label: 'Climb decay',
    min: 0,
    max: 400,
    step: 10,
    hint: 'Metres of climb lost per second when not drifting.',
  },
  {
    key: 'rideTime',
    label: 'Invincibility length',
    min: 2,
    max: 15,
    step: 0.5,
    hint: 'Seconds the rainbow prism makes the walls harmless. Touching one then pushes instead of biting.',
  },
  {
    key: 'rideGain',
    label: 'Wall ride push',
    min: 0,
    max: 0.3,
    step: 0.01,
    hint: 'Speed gained per second of wall contact while invincible, as a fraction. The excess settles at this over the speed gain minus this.',
  },
  {
    key: 'rideChance',
    label: 'Invincibility frequency',
    min: 0,
    max: 0.02,
    step: 0.0005,
    hint: 'Odds per 12 m segment of an invincibility pickup, past the opening stretch.',
  },
  {
    key: 'fuelCan',
    label: 'Fuel per can',
    min: 5,
    max: 100,
    step: 5,
    hint: 'Points a can returns to the tank, which runs 0 to 100.',
  },
  {
    key: 'fuelCanChance',
    label: 'Fuel can frequency',
    min: 0,
    max: 0.03,
    step: 0.0005,
    hint: 'Odds per 12 m segment of a can, past the opening stretch. Difficulty lowers it.',
  },
  {
    key: 'fuelDrySpeed',
    label: 'Dry cruise speed',
    min: 20,
    max: 258,
    step: 2,
    hint: 'Ceiling on the cruise speed, in m/s, while the tank is empty. 56 is about 200 km/h; 258 is no ceiling.',
  },
  {
    key: 'supTime',
    label: 'Super boost length',
    min: 1,
    max: 10,
    step: 0.5,
    hint: 'Seconds a super boost lasts, found or earned. The window in which to climb to the surge.',
  },
  {
    key: 'centri',
    label: 'Centrifugal force',
    min: 0,
    max: 0.14,
    step: 0.002,
    hint: 'Outward push in corners. The most sensitive value here.',
  },
  {
    key: 'bankAssist',
    label: 'Banking assist',
    min: 0,
    max: 1,
    step: 0.02,
    hint: 'How much of the track banking actually pulls you back in.',
  },
  {
    key: 'bankScale',
    label: 'Corner banking',
    min: 0,
    max: 1.3,
    step: 0.02,
    hint: 'Share of the balance angle applied to the track. At 1 corners almost drive themselves.',
  },
  {
    key: 'curveLoad',
    label: 'Corner severity',
    min: 8,
    max: 40,
    step: 1,
    hint: 'Target lateral load in corners, in m/s². Track curvature adapts to speed to hold it.',
  },
  {
    key: 'climbRate',
    label: 'Relief',
    min: 5,
    max: 45,
    step: 1,
    hint: 'Maximum vertical speed of the track, in m/s. Sets the size of crests and ramps.',
  },
  {
    key: 'launchScale',
    label: 'Jump strength',
    min: 0,
    max: 1.2,
    step: 0.02,
    hint: 'Share of a crest converted into lift. At zero, no jumps at all.',
  },
  {
    key: 'rollChance',
    label: 'Corkscrew frequency',
    min: 0,
    max: 0.5,
    step: 0.01,
    hint: 'Odds that a new stretch rolls into a corkscrew.',
  },
  {
    key: 'stripeEvery',
    label: 'Chevron spacing',
    min: 1,
    max: 5,
    step: 0.25,
    hint: 'Segments between two chevrons, one segment is 12 m. Below 2 the pattern strobes at top speed.',
  },
  {
    key: 'hullImpact',
    label: 'Damage per impact',
    min: 0,
    max: 5,
    step: 0.05,
    hint: 'Points lost per metre per second of impact into a wall.',
  },
  {
    key: 'hullRegen',
    label: 'Repair rate',
    min: 0,
    max: 6,
    step: 0.1,
    hint: 'Damage points recovered per second.',
  },
  {
    key: 'coinChance',
    label: 'Coin trail frequency',
    min: 0,
    max: 0.08,
    step: 0.002,
    hint: 'Odds per 12 m segment of starting a coin trail.',
  },
  {
    key: 'multDecay',
    label: 'Multiplier decay',
    min: 0.02,
    max: 0.4,
    step: 0.01,
    hint: 'Share of the multiplier lost per second. Halved above the top speed tier.',
  },
  {
    key: 'multWallCut',
    label: 'Wall multiplier cut',
    min: 0,
    max: 1,
    step: 0.05,
    hint: 'Fraction of the multiplier kept after hitting a wall. 0.5 halves it.',
  },
  {
    key: 'multMax',
    label: 'Multiplier cap',
    min: 5,
    max: 80,
    step: 1,
    hint: 'Upper limit of the multiplier.',
  },
  {
    key: 'fastLane',
    label: 'Fast lane speed',
    min: 60,
    max: 340,
    step: 2,
    hint: 'Speed in m/s above which the multiplier decays half as fast. 278 is 1000 km/h.',
  },
  {
    key: 'fixChance',
    label: 'Repair frequency',
    min: 0,
    max: 0.02,
    step: 0.0005,
    hint: 'Odds per 12 m segment of a repair pickup.',
  },
  {
    key: 'supChance',
    label: 'Super boost frequency',
    min: 0,
    max: 0.02,
    step: 0.0005,
    hint: 'Odds per 12 m segment of a super boost pickup.',
  },
  {
    key: 'rollNodes',
    label: 'Corkscrew length',
    min: 16,
    max: 90,
    step: 2,
    hint: 'Segments per full turn. Lower is more violent.',
  },
  {
    key: 'boostFactor',
    label: 'Boost power',
    min: 1,
    max: 2,
    step: 0.02,
    hint: 'Multiplier applied to target speed while boosting.',
  },
  {
    key: 'boostDrain',
    label: 'Boost drain',
    min: 5,
    max: 60,
    step: 1,
    hint: 'Reserve points spent per second. The reserve runs 0 to 100.',
  },
  {
    key: 'boostRecharge',
    label: 'Boost recharge',
    min: 2,
    max: 40,
    step: 1,
    hint: 'Reserve points regained per second outside boost.',
  },
  {
    key: 'wallPenalty',
    label: 'Wall penalty',
    min: 0,
    max: 1,
    step: 0.02,
    hint: 'Speed lost while scraping a wall.',
  },

  /* Ajoutés en réponse à la dette 11 : les clés qu'un concepteur cherche en
     premier, d'après la liste que la dette donnait elle-même. Le reste — les
     constantes de génération fines, les paliers internes — reste accessible en
     vif par `__gsNext.tuning()` sans encombrer le panneau. */
  {
    key: 'camDist',
    label: 'Camera distance',
    min: 8,
    max: 40,
    step: 0.5,
    hint: 'Metres behind the ship. Close feels faster, far reads better.',
  },
  {
    key: 'camHeight',
    label: 'Camera height',
    min: 2,
    max: 12,
    step: 0.25,
    hint: 'Metres above the track at the camera anchor.',
  },
  {
    key: 'fovBase',
    label: 'Field of view',
    min: 50,
    max: 110,
    step: 1,
    hint: 'Base angle in degrees. Speed and boost widen it further.',
  },
  {
    key: 'fovSpeed',
    label: 'FOV speed gain',
    min: 0,
    max: 40,
    step: 1,
    hint: 'Extra degrees at top speed. The cheap way to sell velocity.',
  },
  {
    key: 'pickRadius',
    label: 'Pickup radius',
    min: 1,
    max: 8,
    step: 0.1,
    hint: 'Lateral metres within which a coin or repair is collected.',
  },
  {
    key: 'airGravity',
    label: 'Jump gravity',
    min: 1,
    max: 8,
    step: 0.1,
    hint: 'Multiple of g pulling the ship down mid-air. Lower means longer jumps.',
  },
  {
    key: 'hullScrape',
    label: 'Scrape damage',
    min: 0,
    max: 40,
    step: 1,
    hint: 'Hull points lost per second while grinding a wall.',
  },
  {
    key: 'boostMin',
    label: 'Boost minimum',
    min: 0,
    max: 50,
    step: 1,
    hint: 'Reserve needed before boost will engage at all.',
  },
];
